import { Decision } from '@/Decision/decision.entity';
import { Entry } from '@/Entry/entry.entity';
import { Criterion } from '@/Litigation/criterion.entity';
import { Litigation } from '@/Litigation/litigation.entity';
import { Member } from '@/Member/member.entity';

export enum AnalysisStatus {
    NOT_STARTED = 'NOT_STARTED',
    ONGOING = 'ONGOING',
    CHECKING = 'CHECKING',
    TO_REVIEW = 'TO_REVIEW',
    APPROVED = 'APPROVED',
}

/**
 * Display-only markers the API hydrates into an analysis' status timeline so it can show who
 * uploaded the decision and who the analysis was assigned to.
 *
 * These are not workflow states: they are never persisted, never part of a transition, and must
 * stay out of anything that reasons about `AnalysisStatus` (permissions, filters, transitions).
 */
export enum SyntheticAnalysisStatus {
    UPLOADED_BY = 'UPLOADED_BY',
    ASSIGNED_TO = 'ASSIGNED_TO',
}

/** Anything that can appear as a row in the history timeline. */
export type TimelineStatus = AnalysisStatus | SyntheticAnalysisStatus;

export enum AnalysisErrorStatuses {
    UNPROCESSED = 'UNPROCESSED',
    IGNORED = 'IGNORED',
    FIXED = 'FIXED',
}

export type AnalysisError = {
    criterion: Criterion;
    occurrence: number;
    status: AnalysisErrorStatuses;
    message: string;
};

export type AnalysisSort = {
    prop: string;
    order: 'ascending' | 'descending';
};

export const allowedAnalysisSortProps = ['status', 'updatedAt'];

export type AnalysisShort = {
    analysisId: string;
    createdAt: string;
    updatedAt: string;
    status: AnalysisStatus;
    // The history timeline, newest-first as ordered by the API. Carries the synthetic
    // provenance markers alongside real transitions, hence TimelineStatus rather than
    // AnalysisStatus — anything reasoning about workflow state wants `status` above.
    statuses: { status: TimelineStatus; createdAt: Date; member?: Member }[];
};

export type Analysis = AnalysisShort & {
    decision: Decision;
    litigation: Litigation;
    owner: Member;
    entries: Entry[];
    foremostEntries: Entry[];
    errors: AnalysisError[];
    // errors populated by the analyses-list endpoint; errors are only (re)computed backend-side per
    // analysis at status change
    errorCount: number;
};

export enum BulkApproveFailureReason {
    // UUID not in the database.
    NOT_FOUND = 'not_found',
    // Caller lacks approval permission for that analysis.
    NOT_AUTHORIZED = 'not_authorized',
    // Error check found unresolved issues => status rolled back to CHECKING.
    UNPROCESSED_ERRORS = 'unprocessed_errors',
}

export type BulkApproveFailure = {
    analysisId: string;
    reason: BulkApproveFailureReason;
};

export type BulkApproveResult = {
    approved: string[];
    failed: BulkApproveFailure[];
};

// Raw payload returned by POST /analyses/bulk-approve, before normalisation. The endpoint
// wraps its result in a JSON:API envelope but the body is a plain object (not a deserializable
// resource collection), and failures are keyed by `id` rather than `analysisId`.
export type BulkApproveResponse = {
    data: {
        approved: string[];
        failed: { id: string; reason: BulkApproveFailureReason }[];
    };
};
